/* Toast system — ToastProvider context + useToast hook for push notifications.
   Ported from lib/components.jsx (claude_design project 5c0836b6-facf-49dd-9c78-62afe3f7bc12). */

import React, {
  useState,
  useRef,
  useCallback,
  createContext,
  useContext,
} from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  type?: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

export type PushToast = (options: ToastOptions) => void;

const ToastCtx = createContext<PushToast | null>(null);

interface ToastEntry extends ToastOptions {
  id: string;
}

const ICONS: Record<ToastType, IconName> = {
  success: 'check-circle',
  error: 'x-circle',
  info: 'info',
  warning: 'alert-triangle',
};

const COLOR: Record<ToastType, string> = {
  success: 'green',
  error: 'red',
  info: 'blue',
  warning: 'amber',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const push = useCallback<PushToast>((t) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((p) => [...p, { id, ...t }]);
    timersRef.current[id] = setTimeout(() => {
      setToasts((p) => p.filter((x) => x.id !== id));
      delete timersRef.current[id];
    }, t.duration ?? 3600);
  }, []);

  const dismiss = (id: string) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((p) => p.filter((x) => x.id !== id));
  };

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-wrap">
        {toasts.map((t) => {
          const type: ToastType = t.type ?? 'info';
          const color = COLOR[type];
          return (
            <div key={t.id} className={'toast t-' + type}>
              <span
                style={{
                  color: `var(--${color}-solid)`,
                  marginTop: 1,
                }}
              >
                <Icon name={ICONS[type]} size={17} />
              </span>
              <div className="grow">
                <div className="toast-title">{t.title}</div>
                {t.message && <div className="toast-msg">{t.message}</div>}
              </div>
              <button
                className="btn btn-ghost btn-icon btn-sm"
                style={{ height: 22, width: 22, marginTop: -1 }}
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
              >
                <Icon name="x" size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop: PushToast = () => {};

export function useToast(): PushToast {
  return useContext(ToastCtx) ?? noop;
}
